import { Link } from "react-router-dom";
import { ScrollText, ArrowLeft, Mail, Phone, Globe2 } from "lucide-react";
import { SUPPORT_NUMBERS } from "@/lib/constants";

/**
 * /mentions-legales — Legal notice page (FR).
 * Static informational page. Replace placeholders (raison sociale, RCS, etc.) with the real legal
 * information once the company is registered.
 */
export default function MentionsLegales() {
  return (
    <div className="min-h-screen sl-hero-bg pt-28 pb-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/compte" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[var(--sl-primary)] mb-6" data-testid="legal-back">
          <ArrowLeft size={14} /> Retour au compte
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <span className="w-12 h-12 rounded-2xl bg-[var(--sl-primary)]/10 grid place-items-center text-[var(--sl-primary)]"><ScrollText size={22} /></span>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold">Mentions légales</h1>
            <p className="text-gray-600 text-sm mt-1">Informations légales relatives au site Swiftlink-Africa.</p>
          </div>
        </div>

        <div className="sl-card space-y-6 text-sm leading-relaxed text-gray-700" data-testid="legal-content">
          <Section title="1. Éditeur du site">
            <p>
              Le site <strong>Swiftlink-Africa</strong> (ci-après « le Site »), accessible à l&apos;adresse{" "}
              <a href="https://swiftlink-africa.com" className="text-[var(--sl-primary)] hover:underline">https://swiftlink-africa.com</a>,
              est édité par Swiftlink-Africa, agent indépendant de transfert d&apos;argent par Mobile Money
              opérant sur les corridors Togo → Gabon, Gabon → France, Gabon → Rwanda et France → Gabon.
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li><span className="font-semibold">Raison sociale :</span> Swiftlink-Africa (à compléter avec la forme juridique enregistrée).</li>
              <li><span className="font-semibold">Représentant légal :</span> Ameyayra Koffi Mawuyram.</li>
              <li><span className="font-semibold">Siège social :</span> à compléter (à indiquer une fois immatriculé).</li>
              <li><span className="font-semibold">Identifiant fiscal :</span> à compléter.</li>
            </ul>
          </Section>

          <Section title="2. Contact">
            <p>Vous pouvez nous contacter via les canaux suivants :</p>
            <ul className="mt-2 space-y-1.5">
              {SUPPORT_NUMBERS.map((s) => (
                <li key={s.country} className="flex items-center gap-2">
                  <Phone size={14} className="text-[var(--sl-primary)]" />
                  <span className="font-medium">{s.flag} {s.country} :</span>
                  <a href={s.waLink} target="_blank" rel="noreferrer" className="font-mono text-[var(--sl-primary)] hover:underline">{s.phone}</a>
                </li>
              ))}
              <li className="flex items-center gap-2">
                <Mail size={14} className="text-[var(--sl-primary)]" />
                <a href="mailto:contact@swiftlink-africa.com" className="text-[var(--sl-primary)] hover:underline">contact@swiftlink-africa.com</a>
              </li>
              <li className="flex items-center gap-2">
                <Globe2 size={14} className="text-[var(--sl-primary)]" />
                <a href="https://swiftlink-africa.com" className="text-[var(--sl-primary)] hover:underline">swiftlink-africa.com</a>
              </li>
            </ul>
          </Section>

          <Section title="3. Hébergement">
            <p>
              Le Site est hébergé sur une infrastructure Cloud sécurisée (conteneurs Kubernetes
              managés). Les données utilisateurs (transactions, justificatifs) sont stockées dans une
              base MongoDB et un service d&apos;objets chiffré côté serveur.
            </p>
          </Section>

          <Section title="4. Propriété intellectuelle">
            <p>
              Le nom <strong>Swiftlink-Africa</strong>, le logo, l&apos;ensemble des textes, illustrations,
              icônes et éléments graphiques du Site sont la propriété exclusive de Swiftlink-Africa.
              Toute reproduction, représentation, modification ou exploitation, totale ou partielle,
              sans autorisation écrite préalable est strictement interdite.
            </p>
          </Section>

          <Section title="5. Responsabilité">
            <p>
              Swiftlink-Africa met tout en œuvre pour assurer l&apos;exactitude des informations
              publiées sur le Site. Toutefois, ces informations sont fournies à titre indicatif et
              sont susceptibles d&apos;évoluer. Swiftlink-Africa ne peut être tenu responsable des
              erreurs, omissions, ou de l&apos;impossibilité d&apos;accéder au Site.
            </p>
            <p className="mt-2">
              Les transferts d&apos;argent dépendent d&apos;opérateurs tiers (Moov, TMoney, Airtel, MTN,
              Tigo, PayPal, Wero, banques) dont Swiftlink-Africa n&apos;a pas le contrôle direct.
            </p>
          </Section>

          <Section title="6. Données personnelles">
            <p>
              Les données collectées (identité, téléphone, justificatifs de paiement, pièce d&apos;identité
              pour les transactions ≥ 300&nbsp;000 FCFA) sont utilisées exclusivement pour exécuter
              les transferts demandés et respecter les obligations de lutte contre le blanchiment de
              capitaux (LCB-FT).
            </p>
            <p className="mt-2">
              Conformément au Règlement Général sur la Protection des Données (RGPD) et aux lois
              applicables au Togo et au Gabon, vous disposez d&apos;un droit d&apos;accès, de rectification,
              d&apos;effacement, et de portabilité de vos données personnelles. Vous pouvez exercer ces
              droits en écrivant à <a href="mailto:contact@swiftlink-africa.com" className="text-[var(--sl-primary)] hover:underline">contact@swiftlink-africa.com</a>.
            </p>
            <p className="mt-2">
              Voir également notre page <Link to="/conditions" className="text-[var(--sl-primary)] hover:underline">Conditions d&apos;utilisation</Link> pour le détail.
            </p>
          </Section>

          <Section title="7. Cookies">
            <p>
              Le Site utilise uniquement des cookies techniques strictement nécessaires à son
              fonctionnement (authentification, suivi de transaction). Aucun cookie publicitaire ou
              de mesure d&apos;audience tiers n&apos;est déposé.
            </p>
          </Section>

          <Section title="8. Droit applicable">
            <p>
              Les présentes mentions légales sont régies par le droit en vigueur au Togo. Tout
              litige relatif au Site relève de la compétence exclusive des tribunaux compétents.
            </p>
          </Section>

          <div className="pt-2 text-xs text-gray-400">
            Dernière mise à jour : février 2026.
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/conditions" className="sl-btn-outline text-sm" data-testid="legal-go-cgu">Conditions d&apos;utilisation</Link>
          <Link to="/faq" className="sl-btn-outline text-sm" data-testid="legal-go-faq">Support · FAQ</Link>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading font-bold text-base mb-2 text-gray-900">{title}</h2>
      <div className="text-sm leading-relaxed">{children}</div>
    </section>
  );
}
